import { useMemo } from 'react'
import { useInfiniteQuery, useMutation } from '@tanstack/react-query'
import { useSearchParams } from 'react-router-dom'
import { ApiError } from '@/lib/api'
import { QK, invalidateFor } from '@/lib/query-keys'
import { DsToast } from '@/ds'
import { leaseApi, LEASE_ENDING_SOON_DAYS, type LeaseListParams } from '@/features/lease/lease.api'
import { LEASE_FILTERS, type LeaseFilter } from '@/features/lease/lease-utils'
import { leaseStrings as s } from '@/features/lease/strings'
import { useListCountry } from '@/features/view-country/useViewCountry'

export const leaseKeys = {
  list: (params: LeaseListParams) => [...QK.leases, 'list', params] as const,
  detail: (id: string) => [...QK.leases, 'detail', id] as const,
  rents: (id: string) => [...QK.rents, 'lease', id] as const,
}

const PAGE = 25

function paramsFor(filter: LeaseFilter, search: string, propertyId?: string, country?: string): LeaseListParams {
  const base: LeaseListParams = { size: PAGE, facets: true, ...(search ? { search } : {}), ...(propertyId ? { propertyId } : {}), ...(country ? { country } : {}) }
  switch (filter) {
    case 'active':
      return { ...base, status: 'active' }
    case 'endingSoon':
      return { ...base, endingInDays: LEASE_ENDING_SOON_DAYS }
    case 'paymentPending':
      return { ...base, status: 'payment-pending' }
    default:
      return base
  }
}

export function useLeases() {
  const [params, setParams] = useSearchParams()
  const f = params.get('filter')
  const filter: LeaseFilter = LEASE_FILTERS.includes(f as LeaseFilter) ? (f as LeaseFilter) : 'all'
  const search = params.get('q') ?? ''
  const propertyId = params.get('propertyId') ?? ''
  // `?country=`, defaulting to the viewing country; "All countries" sends none (LP-491).
  const countryFilter = useListCountry()
  const { country } = countryFilter
  const listParams = useMemo(() => paramsFor(filter, search, propertyId || undefined, country), [filter, search, propertyId, country])

  const query = useInfiniteQuery({
    queryKey: leaseKeys.list(listParams),
    queryFn: ({ pageParam }) => leaseApi.list({ ...listParams, page: pageParam }),
    initialPageParam: 1,
    getNextPageParam: (last) => (last.page * last.size < last.total ? last.page + 1 : undefined),
  })
  const items = useMemo(() => query.data?.pages.flatMap((p) => p.items) ?? [], [query.data])
  const facets = query.data?.pages[0]?.facets
  const total = query.data?.pages[0]?.total ?? 0

  const set = (key: 'filter' | 'q' | 'propertyId', value: string) => {
    const next = new URLSearchParams(params)
    if (!value || (key === 'filter' && value === 'all')) next.delete(key)
    else next.set(key, value)
    setParams(next, { replace: true })
  }

  const remind = useMutation({
    mutationFn: (leaseId: string) => leaseApi.remind(leaseId),
    onSuccess: () => DsToast.success(s.remindSent),
    onError: (e) => DsToast.error(e instanceof ApiError ? e.message : s.somethingWentWrong),
  })

  const deletePending = useMutation({
    mutationFn: async (leaseId: string) => {
      await leaseApi.cancel(leaseId)
      await invalidateFor('tenant', 'property')
    },
    onSuccess: () => DsToast.success(s.deleted),
    onError: (e) => DsToast.error(e instanceof ApiError ? e.message : s.somethingWentWrong),
  })

  return {
    ...query,
    countryFilter,
    items,
    facets,
    total,
    filter,
    search,
    setFilter: (v: LeaseFilter) => set('filter', v),
    setSearch: (v: string) => set('q', v),
    propertyId,
    /** Scope to one property; '' clears it. */
    setPropertyId: (v: string) => set('propertyId', v),
    reset: () => {
      const next = new URLSearchParams(params)
      for (const k of ['filter', 'q', 'propertyId']) next.delete(k)
      setParams(next, { replace: true })
    },
    remind,
    deletePending,
  }
}
